<template>
  <DialogLayout class="flow-db-record-approval"
                title="流程审批管理"
                v-loading="loading"
                element-loading-text="数据加载中，请稍等...">
    <template v-if="approvalInfo">
      <div class="approval-main">
        <div class="approval-info">
          <div class="header-title">
            <span class="process-name">{{ approvalInfo.processDefinitionName }}</span>
            <el-tag v-if="approvalInfo.ended" size="mini" type="info">已结束</el-tag>
            <el-tag v-else-if="approvalInfo.suspended" size="mini" type="warning">已停用</el-tag>
            <el-tag v-else size="mini" type="success">启用中</el-tag>
            <el-tag v-if="approvalInfo.flowNo" size="mini">{{ approvalInfo.flowNo }}</el-tag>
          </div>
          <div class="header-info">
            <span class="info-item">
              <span class="info-label">
                <i class="el-icon-user"></i>
                <span>发起人：</span>
              </span>
              <span class="info-value">{{ approvalInfo.startUser || '-' }}</span>
            </span>
            <el-divider direction="vertical"></el-divider>
            <span class="info-item">
              <span class="info-label">
                <i class="el-icon-time"></i>
                <span>提交时间：</span>
              </span>
              <span class="info-value">{{ approvalInfo.startTime || '-' }}</span>
            </span>
            <el-divider direction="vertical"></el-divider>
            <span class="info-item">
              <span class="info-label">
                <i class="el-icon-location-outline"></i>
                <span>当前节点：</span>
              </span>
              <span class="info-value">{{ approvalInfo.currentActivityName || '-' }}</span>
            </span>
            <el-divider direction="vertical"></el-divider>
            <span class="info-item">
              <span class="info-label">
                <i class="el-icon-files"></i>
                <span>审批状态：</span>
              </span>
              <el-tag v-if="approvalInfo.bizStatus"
                      size="mini"
                      :type="bizStatusTagType">{{ approvalInfo.bizStatus.name }}</el-tag>
              <span v-else class="info-value">-</span>
              <el-tag v-if="approvalInfo.delegated"
                      size="mini"
                      type="warning"
                      style="margin-left: 5px;">已委派</el-tag>
            </span>
          </div>
        </div>
        <!-- Tab 切换区域 -->
        <el-tabs class="approval-tabs" v-model="activeTab" @tab-click="handleTabChange">
          <!-- 表单数据 -->
          <el-tab-pane label="表单数据" name="flowForm" lazy>
            <div class="tab-content">
              <FlowDbRecordApprovalForm :id="rowData.id" :active="tabActivated.flowForm"></FlowDbRecordApprovalForm>
            </div>
          </el-tab-pane>

          <!-- 流转记录 -->
          <el-tab-pane label="流转记录" name="flowHistory" lazy>
            <div class="tab-content">
              <FlowDbRecordApprovalHistory :id="rowData.id" :active="tabActivated.flowHistory"></FlowDbRecordApprovalHistory>
            </div>
          </el-tab-pane>

          <!-- 流程图 -->
          <el-tab-pane label="流程图" name="flowDiagram" lazy>
            <div class="tab-content">
              <FlowDbRecordApprovalDiagram :id="rowData.id" :active="tabActivated.flowDiagram"></FlowDbRecordApprovalDiagram>
            </div>
          </el-tab-pane>
        </el-tabs>

        <!-- 操作按钮区域 -->
        <ActionGroup class="approval-actions">
          <template v-if="!approvalInfo.ended">
            <ActionItem>
              <el-button type="text" size="small" icon="el-icon-bell" @click="handleUrge">催办</el-button>
            </ActionItem>
            <ActionItem>
              <el-button type="text" size="small" icon="el-icon-position" @click="handleTransfer">转办</el-button>
            </ActionItem>
            <ActionItem v-if="!approvalInfo.delegated">
              <el-tooltip :disabled="canParallelMulti" content="只有并行会签的任务才能使用" placement="top">
                <el-button type="text" size="small" icon="el-icon-circle-plus-outline"
                           :disabled="!canParallelMulti" @click="handleParallelMulti">加签</el-button>
              </el-tooltip>
            </ActionItem>
            <ActionItem>
              <el-button type="text" size="small" icon="el-icon-chat-line-round" @click="handleComment">评论</el-button>
            </ActionItem>
            <ActionItem>
              <el-button type="text" size="small" icon="el-icon-back" @click="handleRollback">回退</el-button>
            </ActionItem>
            <ActionItem>
              <el-button type="text" size="small" icon="el-icon-refresh-left" @click="handleRevoke">撤销</el-button>
            </ActionItem>
          </template>
          <ActionItem position="right">
            <el-button type="text" size="small" class="btn-warning" icon="el-icon-s-promotion" @click="handleResubmit">重提</el-button>
          </ActionItem>
          <ActionItem v-if="approvalInfo.ended">
            <span class="flow-ended-text"><i class="el-icon-circle-check"></i><span>流程已结束，相关审批任务已完成</span></span>
          </ActionItem>
          <ActionItem v-if="!approvalInfo.ended" position="right">
            <el-tooltip placement="top">
              <template slot="content">
                <i class="el-icon-warning"></i>
                <span>版本迁移服务在一些情形下存在问题，非必须请勿使用！</span>
              </template>
              <el-button type="text" size="small" icon="el-icon-connection" @click="handleMigrate">迁移流程</el-button>
            </el-tooltip>
          </ActionItem>
          <ActionItem v-if="!approvalInfo.ended" position="right">
            <el-button type="text" size="small"
                       :icon="isSuspended ? 'el-icon-video-play' : 'el-icon-video-pause'"
                       @click="handleUpdateStatus">{{ isSuspended ? '启用' : '停用' }}流程</el-button>
          </ActionItem>
          <ActionItem position="right">
            <el-button type="text" size="small" class="btn-danger" icon="el-icon-delete" @click="handleDelete">删除流程</el-button>
          </ActionItem>
        </ActionGroup>
      </div>
    </template>
    <template v-else-if="!loading">
      <Hint type="error">
        <div>流程数据加载失败</div>
        <el-button size="mini" type="text" @click="loadApprovalInfo">重新加载</el-button>
      </Hint>
    </template>

    <!-- 停用/启用确认弹窗 -->
    <DataGridDialog :title="isSuspended ? '启用确认' : '停用确认'"
                    :visible.sync="updateStatusDialog.visible"
                    width="400px"
                    custom-class="flow-db-record-approval-dialog">
      <div class="confirm-dialog-content">
        <div class="confirm-message">
          <i class="el-icon-warning"></i>
          <span>确定要{{ isSuspended ? '启用' : '停用' }}该流程实例吗？</span>
        </div>
        <div class="confirm-hint">
          <p class="hint-text">
            {{ isSuspended ? '启用后，该流程实例将恢复正常运行' : '停用后，该流程实例将被挂起，无法继续流转' }}
          </p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="updateStatusDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :loading="updateStatusDialog.submitting"
                   @click="confirmUpdateStatus">确定</el-button>
      </div>
    </DataGridDialog>

    <!-- 删除确认弹窗 -->
    <DataGridDialog title="删除确认"
                    :visible.sync="removeDialog.visible"
                    width="450px"
                    custom-class="flow-db-record-approval-dialog">
      <div class="confirm-dialog-content">
        <div class="confirm-message">
          <i class="el-icon-warning"></i>
          <span>确定要删除该流程实例吗？</span>
        </div>
        <div class="confirm-form">
          <el-form label-width="80px" size="small">
            <el-form-item label-width="0">
              <el-input v-model="removeDialog.reason"
                        type="textarea"
                        :rows="3"
                        placeholder="请输入删除原因"></el-input>
            </el-form-item>
          </el-form>
        </div>
        <div class="confirm-option">
          <el-checkbox v-model="removeDialog.includeHistoric">
            删除历史
          </el-checkbox>
          <p class="hint-text">删除历史将会同时删除流程的历史记录</p>
        </div>
        <div class="confirm-option">
          <el-checkbox v-model="removeDialog.forceDelete">
            强制删除
          </el-checkbox>
          <p class="hint-text">强制删除将会忽略业务中的数据异常</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="removeDialog.visible = false">取消</el-button>
        <el-button type="danger"
                   size="small"
                   :loading="removeDialog.submitting"
                   @click="confirmDelete">确定删除</el-button>
      </div>
    </DataGridDialog>

    <!-- 催办弹窗 -->
    <DataGridDialog title="流程催办"
                    :visible.sync="urgeDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-urge-dialog"
                    :close-on-click-modal="false">
      <div v-loading="urgeDialog.loading"
           element-loading-text="加载中..."
           class="urge-content">
        <el-form label-position="top"
                 size="small">
          <el-form-item label="催办人员">
            <el-select v-model="urgeDialog.targetTaskIds"
                       multiple
                       placeholder="请选择催办人员"
                       style="width: 100%">
              <el-option v-for="task in urgeDialog.taskList"
                         :key="task.id"
                         :label="task.assigneeName"
                         :value="task.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="备注信息">
            <el-input v-model="urgeDialog.comment"
                      type="textarea"
                      :rows="3"
                      :maxlength="200"
                      show-word-limit
                      placeholder="请输入备注信息">
            </el-input>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="urgeDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="urgeDialog.targetTaskIds.length === 0"
                   :loading="urgeDialog.submitting"
                   @click="confirmUrge">确定催办</el-button>
      </div>
    </DataGridDialog>

    <!-- 转办弹窗 -->
    <DataGridDialog title="流程转办"
                    :visible.sync="transferDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-transfer-dialog"
                    :close-on-click-modal="false">
      <div v-loading="transferDialog.loading"
           element-loading-text="加载中..."
           class="transfer-content">
        <FlowApprovalForm ref="transferForm">
          <template slot="prepend">
            <el-form-item label="目标人员">
              <el-select v-model="transferDialog.targetTaskId"
                         placeholder="请选择目标人员"
                         style="width: 100%">
                <el-option v-for="task in transferDialog.taskList"
                           :key="task.id"
                           :label="task.assigneeName"
                           :value="task.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="转交人员">
              <div class="member-select-area">
                <el-button size="mini"
                           icon="el-icon-plus"
                           round
                           @click="selectTransferMember"></el-button>
                <template v-if="transferDialog.transferUser.length">
                  <member-items v-model="transferDialog.transferUser"
                                :single="true"
                                class="transfer-member-items"></member-items>
                </template>
                <template v-else>
                  <span class="transfer-placeholder">请设置要转交给谁</span>
                </template>
              </div>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="transferDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="transferDialog.transferUser.length === 0 || !transferDialog.targetTaskId"
                   :loading="transferDialog.submitting"
                   @click="confirmTransfer">确定转办</el-button>
      </div>
    </DataGridDialog>

    <!-- 转办选人组件 -->
    <member-picker title="请选择转交人员"
                   ref="transferMemberPicker"
                   :selected="transferDialog.transferUser"
                   @ok="onTransferMemberSelected"/>

    <!-- 加签弹窗 -->
    <DataGridDialog title="流程加签"
                    :visible.sync="parallelMultiDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-parallel-multi-dialog"
                    :close-on-click-modal="false">
      <div v-loading="parallelMultiDialog.loading"
           element-loading-text="加载中..."
           class="parallel-multi-content">
        <FlowApprovalForm ref="parallelMultiForm">
          <template slot="prepend">
            <el-form-item label="目标人员">
              <el-select v-model="parallelMultiDialog.targetTaskId"
                         placeholder="请选择目标人员"
                         style="width: 100%">
                <el-option v-for="task in parallelMultiDialog.taskList"
                           :key="task.id"
                           :label="task.assigneeName"
                           :value="task.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="选择人员">
              <div class="member-select-area">
                <el-button size="mini"
                           icon="el-icon-plus"
                           round
                           @click="selectParallelMultiMember"></el-button>
                <template v-if="parallelMultiDialog.multiAddUser.length">
                  <member-items v-model="parallelMultiDialog.multiAddUser"
                                class="parallel-multi-member-items"></member-items>
                </template>
                <template v-else>
                  <span class="parallel-multi-placeholder">请选择加签人员</span>
                </template>
              </div>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="parallelMultiDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="parallelMultiDialog.multiAddUser.length === 0 || !parallelMultiDialog.targetTaskId"
                   :loading="parallelMultiDialog.submitting"
                   @click="confirmParallelMulti">确定加签</el-button>
      </div>
    </DataGridDialog>

    <!-- 加签选人组件 -->
    <member-picker title="请选择加签人员"
                   ref="parallelMultiMemberPicker"
                   :selected="parallelMultiDialog.multiAddUser"
                   @ok="onParallelMultiMemberSelected"/>

    <!-- 评论弹窗 -->
    <DataGridDialog title="添加评论"
                    :visible.sync="commentDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-comment-dialog"
                    :close-on-click-modal="false">
      <div v-loading="commentDialog.loading"
           element-loading-text="加载中..."
           class="comment-content">
        <FlowApprovalForm ref="commentForm"
                          comment-label="评论内容"
                          comment-required
                          comment-placeholder="请输入评论内容">
          <template slot="prepend">
            <el-form-item label="评论人员">
              <el-select v-model="commentDialog.targetTaskId"
                         placeholder="请选择评论人员"
                         style="width: 100%">
                <el-option v-for="task in commentDialog.taskList"
                           :key="task.id"
                           :label="task.assigneeName"
                           :value="task.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="commentDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="!commentDialog.targetTaskId"
                   :loading="commentDialog.submitting"
                   @click="confirmComment">确定评论</el-button>
      </div>
    </DataGridDialog>

    <!-- 回退弹窗 -->
    <DataGridDialog title="流程回退"
                    :visible.sync="rollbackDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-rollback-dialog"
                    :close-on-click-modal="false">
      <div v-loading="rollbackDialog.loading"
           element-loading-text="加载中..."
           class="rollback-content">
        <FlowApprovalForm ref="rollbackForm">
          <template slot="prepend">
            <el-form-item label="回退节点">
              <el-select v-model="rollbackDialog.targetNodeId"
                         placeholder="请选择回退节点"
                         style="width: 100%">
                <el-option v-for="node in rollbackDialog.nodes"
                           :key="node.id"
                           :label="node.name"
                           :value="node.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="目标人员">
              <el-select v-model="rollbackDialog.targetTaskId"
                         placeholder="请选择目标人员"
                         style="width: 100%">
                <el-option v-for="task in rollbackDialog.taskList"
                           :key="task.id"
                           :label="task.assigneeName"
                           :value="task.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="rollbackDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="!rollbackDialog.targetNodeId || !rollbackDialog.targetTaskId"
                   :loading="rollbackDialog.submitting"
                   @click="confirmRollback">确定回退</el-button>
      </div>
    </DataGridDialog>

    <!-- 撤销弹窗 -->
    <DataGridDialog title="流程撤销"
                    :visible.sync="revokeDialog.visible"
                    width="500px"
                    custom-class="flow-db-record-revoke-dialog"
                    :close-on-click-modal="false">
      <div v-loading="revokeDialog.loading"
           element-loading-text="加载中..."
           class="revoke-content">
        <FlowApprovalForm ref="revokeForm">
          <template slot="prepend">
            <el-form-item label="目标人员">
              <el-select v-model="revokeDialog.targetTaskId"
                         placeholder="请选择目标人员"
                         style="width: 100%">
                <el-option v-for="task in revokeDialog.taskList"
                           :key="task.id"
                           :label="task.assigneeName"
                           :value="task.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="revokeDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="!revokeDialog.targetTaskId"
                   :loading="revokeDialog.submitting"
                   @click="confirmRevoke">确定撤销</el-button>
      </div>
    </DataGridDialog>

    <!-- 版本迁移弹窗 -->
    <DataGridDialog title="版本迁移"
                    :visible.sync="migrationDialog.visible"
                    width="700px"
                    custom-class="flow-db-record-migration-dialog"
                    :close-on-click-modal="false">
      <div v-loading="migrationDialog.loading" class="migration-content">
        <!-- 版本信息展示 -->
        <div class="version-info-section">
          <div class="version-info-row">
            <div class="version-info-item">
              <span class="version-label">当前版本：</span>
              <template v-if="migrationDialog.sourceVersionInfo">
                <span class="version-name">{{ migrationDialog.sourceVersionInfo.name }}</span>
                <el-tag size="mini">第 {{ migrationDialog.sourceVersionInfo.version }} 版</el-tag>
              </template>
              <template v-else>
                <span class="version-name">-</span>
              </template>
            </div>
            <div class="version-arrow">
              <i class="el-icon-right"></i>
            </div>
            <div class="version-info-item target-version-item">
              <span class="version-label">目标版本：</span>
              <el-select v-model="migrationDialog.targetVersion"
                         placeholder="请选择"
                         size="small"
                         @change="handleTargetVersionChange">
                <el-option v-for="version in migrationDialog.versions"
                           :key="version.id"
                           :label="`第 ${version.version} 版 - ${version.name}`"
                           :value="version.id">
                </el-option>
              </el-select>
            </div>
          </div>
        </div>

        <!-- 节点映射关系 -->
        <template v-if="migrationDialog.targetVersionInfo">
          <div class="mapping-section">
            <div class="section-header">
              <span class="section-title">节点映射关系</span>
              <span class="section-hint">（节点ID相同的已自动关联，可手动修改）</span>
            </div>
            <DataGrid height="260px"
                      size="mini"
                      :data="migrationDialog.activityMapping">
              <DataGridColumn title="原始版本节点"
                              min-width="200">
                <template v-slot="{ row }">
                  {{ row.sourceActivityName }} [{{ row.sourceActivityId }}]
                </template>
              </DataGridColumn>
              <DataGridColumn title=""
                              align="center"
                              width="60">
                <template slot="header">
                  <i class="el-icon-right mapping-arrow"></i>
                </template>
                <template>
                  <i class="el-icon-right mapping-arrow"></i>
                </template>
              </DataGridColumn>
              <DataGridColumn title="目标版本节点"
                              min-width="200">
                <template v-slot="{ row }">
                  <el-select v-model="row.targetActivityId"
                             placeholder="请选择目标节点"
                             size="mini"
                             clearable
                             style="width: 100%"
                             @change="handleMappingChange(row)">
                    <el-option v-for="activity in targetActivities"
                               :key="activity.id"
                               :label="`${activity.name} [${activity.id}]`"
                               :value="activity.id">
                    </el-option>
                  </el-select>
                </template>
              </DataGridColumn>
              <span slot="empty">暂无节点信息</span>
            </DataGrid>
          </div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="migrationDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :disabled="!migrationDialog.targetVersion"
                   :loading="migrationDialog.submitting"
                   @click="confirmMigration">确认迁移</el-button>
      </div>
    </DataGridDialog>
  </DialogLayout>
</template>

<script>
import DialogLayout from '../../../../../widgets/DialogLayout'
import FlowDbRecordApprovalForm from './form'
import FlowDbRecordApprovalHistory from './history'
import FlowDbRecordApprovalDiagram from './diagram'
import Hint from '../../../../../elements/Hint'
import DataGridDialog from '../../../../../elements/grid/DataGridDialog'
import DataGrid from '../../../../../elements/DataGrid'
import DataGridColumn from '../../../../../elements/DataGridColumn'
import FlowApprovalForm from '../../../../../elements/flow/approval/FlowApprovalForm'
import MemberPicker from '../../../../../elements/flow/common/MemberPicker'
import MemberItems from '../../../../../elements/flow/process/MemberItems'
import ActionGroup from '../../../../../widgets/action-group/ActionGroup'
import ActionItem from '../../../../../widgets/action-group/ActionItem'
export default {
  name: 'FlowDbRecordApproval',
  components: {
    DialogLayout,
    FlowDbRecordApprovalForm,
    FlowDbRecordApprovalHistory,
    FlowDbRecordApprovalDiagram,
    Hint,
    DataGridDialog,
    DataGrid,
    DataGridColumn,
    FlowApprovalForm,
    MemberPicker,
    MemberItems,
    ActionGroup,
    ActionItem
  },
  props: {
    rowData: {
      type: Object,
      required: true
    },
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      approvalInfo: null,
      formInfo: null,
      historyInfo: null,
      diagramInfo: null,
      // 当前激活的 tab
      activeTab: 'flowForm',
      // tab 激活状态映射
      tabActivated: {
        flowForm: false,
        flowHistory: false,
        flowDiagram: false
      },
      // 停用/启用确认弹窗
      updateStatusDialog: {
        visible: false,
        submitting: false
      },
      // 删除确认弹窗
      removeDialog: {
        visible: false,
        reason: '',
        includeHistoric: false,
        forceDelete: false,
        submitting: false
      },
      // 版本迁移弹窗
      migrationDialog: {
        visible: false,
        loading: false,
        submitting: false,
        versions: [], // 可选版本列表
        targetVersion: null, // 目标版本ID
        sourceVersionInfo: null, // 原始版本信息
        targetVersionInfo: null, // 目标版本信息
        activityMapping: [] // 节点映射关系
      },
      // 催办弹窗
      urgeDialog: {
        visible: false,
        loading: false,
        submitting: false,
        comment: '',
        taskList: [],
        targetTaskIds: []
      },
      // 转办弹窗
      transferDialog: {
        visible: false,
        loading: false,
        submitting: false,
        taskList: [],
        targetTaskId: null,
        transferUser: []
      },
      // 评论弹窗
      commentDialog: {
        visible: false,
        loading: false,
        submitting: false,
        taskList: [],
        targetTaskId: null
      },
      // 回退弹窗
      rollbackDialog: {
        visible: false,
        loading: false,
        submitting: false,
        nodes: [], // 可回退节点列表
        targetNodeId: null, // 目标节点ID
        taskList: [], // 任务列表
        targetTaskId: null // 目标人员ID
      },
      // 撤销弹窗
      revokeDialog: {
        visible: false,
        loading: false,
        submitting: false,
        taskList: [],
        targetTaskId: null
      },
      // 加签弹窗
      parallelMultiDialog: {
        visible: false,
        loading: false,
        submitting: false,
        taskList: [],
        targetTaskId: null,
        multiAddUser: []
      }
    }
  },
  computed: {
    // 流程是否已停用
    isSuspended () {
      return this.approvalInfo && this.approvalInfo.suspended
    },
    // 是否可以加签（只有并行会签的任务才能使用）
    canParallelMulti () {
      return this.approvalInfo && this.approvalInfo.parallelMulti
    },
    // 业务状态标签类型
    bizStatusTagType () {
      if (this.approvalInfo && this.approvalInfo.bizStatus) {
        switch (this.approvalInfo.bizStatus.code) {
          case '1':
            return 'primary'
          case '2':
            return 'info'
          case '3':
            return 'danger'
          case '4':
            return 'success'
          default:
            break
        }
      }
      return 'info'
    },
    // 目标版本的所有节点列表
    targetActivities () {
      if (this.migrationDialog.targetVersionInfo && this.migrationDialog.targetVersionInfo.activities) {
        return this.migrationDialog.targetVersionInfo.activities
      }
      return []
    }
  },
  methods: {
    // tab 切换事件
    handleTabChange (tab) {
      const tabName = tab.name
      if (!this.tabActivated[tabName]) {
        this.tabActivated[tabName] = true
      }
    },
    // 加载审批信息
    loadApprovalInfo () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/approval',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.approvalInfo = response.data.approvalInfo || {}
              // 重置所有 tab 激活状态
              this.tabActivated.flowForm = false
              this.tabActivated.flowHistory = false
              this.tabActivated.flowDiagram = false
              // 在 nextTick 中将当前 tab 状态置为 true
              this.$nextTick(() => {
                this.tabActivated[this.activeTab] = true
              })
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    // 催办
    handleUrge () {
      // 打开催办弹窗
      this.urgeDialog.visible = true
      this.urgeDialog.loading = true
      this.urgeDialog.submitting = false
      this.urgeDialog.comment = ''
      this.urgeDialog.taskList = []
      this.urgeDialog.targetTaskIds = []

      // 加载任务信息
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/taskInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.urgeDialog.taskList = response.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.urgeDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.urgeDialog.visible = false
      }).finally(() => {
        this.urgeDialog.loading = false
      })
    },
    // 确认催办
    confirmUrge () {
      if (this.urgeDialog.targetTaskIds.length === 0) {
        this.$message.warning('请选择催办人员')
        return
      }
      // 二次确认
      this.$confirm('确定要催办此流程吗？', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.urgeDialog.submitting = true
        this.$ajax({
          url: '/workspace/manager/flow/db/record/process/urge',
          method: 'post',
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            id: this.rowData.id,
            comment: this.urgeDialog.comment,
            targetTaskIds: this.urgeDialog.targetTaskIds
          }
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                this.$message.success(response.data['message'] || '催办成功')
                this.urgeDialog.visible = false
                break
              }
              default: {
                this.$message.warning(response.data['message'])
                break
              }
            }
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.urgeDialog.submitting = false
        })
      }).catch(() => {
        // 取消操作
      })
    },
    // 转办
    handleTransfer () {
      // 打开转办弹窗
      this.transferDialog.visible = true
      this.transferDialog.loading = true
      this.transferDialog.submitting = false
      this.transferDialog.taskList = []
      this.transferDialog.targetTaskId = null
      this.transferDialog.transferUser = []
      // 重置表单
      this.$nextTick(() => {
        if (this.$refs.transferForm) {
          this.$refs.transferForm.reset()
        }
      })

      // 加载任务信息
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/taskInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.transferDialog.taskList = response.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.transferDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.transferDialog.visible = false
      }).finally(() => {
        this.transferDialog.loading = false
      })
    },
    // 选择转办人员
    selectTransferMember () {
      this.$refs.transferMemberPicker.show()
    },
    // 转办人员选择完成
    onTransferMemberSelected (selected) {
      // 只能选一个人
      if (selected && selected.length > 0) {
        this.transferDialog.transferUser = [selected[0]]
      } else {
        this.transferDialog.transferUser = []
      }
    },
    // 加签
    handleParallelMulti () {
      // 打开加签弹窗
      this.parallelMultiDialog.visible = true
      this.parallelMultiDialog.loading = true
      this.parallelMultiDialog.submitting = false
      this.parallelMultiDialog.taskList = []
      this.parallelMultiDialog.targetTaskId = null
      this.parallelMultiDialog.multiAddUser = []
      // 重置表单
      this.$nextTick(() => {
        if (this.$refs.parallelMultiForm) {
          this.$refs.parallelMultiForm.reset()
        }
      })

      // 加载任务信息
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/taskInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.parallelMultiDialog.taskList = response.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.parallelMultiDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.parallelMultiDialog.visible = false
      }).finally(() => {
        this.parallelMultiDialog.loading = false
      })
    },
    // 选择加签人员
    selectParallelMultiMember () {
      this.$refs.parallelMultiMemberPicker.show()
    },
    // 加签人员选择完成
    onParallelMultiMemberSelected (selected) {
      if (selected && selected.length > 0) {
        this.parallelMultiDialog.multiAddUser = selected
      } else {
        this.parallelMultiDialog.multiAddUser = []
      }
    },
    // 确认加签
    confirmParallelMulti () {
      if (this.parallelMultiDialog.multiAddUser.length === 0) {
        this.$message.warning('请选择加签人员')
        return
      }
      if (!this.parallelMultiDialog.targetTaskId) {
        this.$message.warning('请选择目标人员')
        return
      }

      // 校验并获取表单数据
      this.$refs.parallelMultiForm.validate().then(formData => {
        // 二次确认
        this.$confirm('确定要加签此流程吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.parallelMultiDialog.submitting = true
          const targetUser = this.parallelMultiDialog.multiAddUser[0]
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/parallelMulti',
            method: 'post',
            headers: {
              'Content-Type': 'application/json'
            },
            data: this.$extend({
              id: this.rowData.id,
              multiAddUser: targetUser,
              targetTaskId: this.parallelMultiDialog.targetTaskId
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '加签成功')
                  this.parallelMultiDialog.visible = false
                  this.loadApprovalInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.parallelMultiDialog.submitting = false
          })
        }).catch(() => {
          // 取消操作
        })
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 确认转办
    confirmTransfer () {
      if (this.transferDialog.transferUser.length === 0) {
        this.$message.warning('请选择转交人员')
        return
      }
      if (!this.transferDialog.targetTaskId) {
        this.$message.warning('请选择目标人员')
        return
      }

      // 校验并获取表单数据
      this.$refs.transferForm.validate().then(formData => {
        // 二次确认
        this.$confirm('确定要转办此流程吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.transferDialog.submitting = true
          const targetUser = this.transferDialog.transferUser[0]
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/transfer',
            method: 'post',
            headers: {
              'Content-Type': 'application/json'
            },
            data: this.$extend({
              id: this.rowData.id,
              transferUser: targetUser,
              targetTaskId: this.transferDialog.targetTaskId
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '转办成功')
                  this.transferDialog.visible = false
                  this.loadApprovalInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.transferDialog.submitting = false
          })
        }).catch(() => {
          // 取消操作
        })
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 评论
    handleComment () {
      // 打开评论弹窗
      this.commentDialog.visible = true
      this.commentDialog.loading = true
      this.commentDialog.submitting = false
      this.commentDialog.taskList = []
      this.commentDialog.targetTaskId = null
      // 重置表单
      this.$nextTick(() => {
        if (this.$refs.commentForm) {
          this.$refs.commentForm.reset()
        }
      })

      // 加载任务信息
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/taskInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.commentDialog.taskList = response.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.commentDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.commentDialog.visible = false
      }).finally(() => {
        this.commentDialog.loading = false
      })
    },
    // 确认评论
    confirmComment () {
      if (!this.commentDialog.targetTaskId) {
        this.$message.warning('请选择评论人员')
        return
      }

      // 校验并获取表单数据
      this.$refs.commentForm.validate().then(formData => {
        // 二次确认
        this.$confirm('确定要提交评论吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.commentDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/comment',
            method: 'post',
            headers: {
              'Content-Type': 'application/json'
            },
            data: this.$extend({
              id: this.rowData.id,
              targetTaskId: this.commentDialog.targetTaskId
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '评论成功')
                  this.commentDialog.visible = false
                  this.loadApprovalInfo()
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.commentDialog.submitting = false
          })
        }).catch(() => {
          // 取消操作
        })
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 回退
    handleRollback () {
      // 重置回退弹窗状态
      this.rollbackDialog.visible = true
      this.rollbackDialog.loading = true
      this.rollbackDialog.submitting = false
      this.rollbackDialog.nodes = []
      this.rollbackDialog.targetNodeId = null
      this.rollbackDialog.taskList = []
      this.rollbackDialog.targetTaskId = null
      // 重置表单
      this.$nextTick(() => {
        if (this.$refs.rollbackForm) {
          this.$refs.rollbackForm.reset()
        }
      })

      // 并行加载可回退节点列表和任务信息
      Promise.all([
        // 加载可回退节点列表
        this.$ajax({
          url: '/workspace/manager/flow/db/record/process/rollbackNodes',
          method: 'post',
          data: this.$qs.stringify({
            id: this.rowData.id
          })
        }),
        // 加载任务信息
        this.$ajax({
          url: '/workspace/manager/flow/db/record/process/taskInfo',
          method: 'post',
          data: this.$qs.stringify({
            id: this.rowData.id
          })
        })
      ]).then(([rollbackNodesResponse, taskInfoResponse]) => {
        let hasError = false

        // 处理回退节点响应
        if (rollbackNodesResponse.data) {
          switch (rollbackNodesResponse.data['responseCode']) {
            case 'SUCCESS': {
              this.rollbackDialog.nodes = rollbackNodesResponse.data.rollbackNodes || []
              break
            }
            default: {
              this.$message.warning(rollbackNodesResponse.data['message'])
              hasError = true
              break
            }
          }
        }

        // 处理任务信息响应
        if (taskInfoResponse.data) {
          switch (taskInfoResponse.data['responseCode']) {
            case 'SUCCESS': {
              this.rollbackDialog.taskList = taskInfoResponse.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(taskInfoResponse.data['message'])
              hasError = true
              break
            }
          }
        }

        if (hasError) {
          this.rollbackDialog.visible = false
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.rollbackDialog.visible = false
      }).finally(() => {
        this.rollbackDialog.loading = false
      })
    },
    // 确认回退
    confirmRollback () {
      if (!this.rollbackDialog.targetNodeId) {
        this.$message.warning('请选择回退目标节点')
        return
      }
      if (!this.rollbackDialog.targetTaskId) {
        this.$message.warning('请选择目标人员')
        return
      }

      // 校验并获取表单数据
      this.$refs.rollbackForm.validate().then(formData => {
        // 二次确认
        this.$confirm('确定要回退此流程吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.rollbackDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/rollback',
            method: 'post',
            headers: {
              'Content-Type': 'application/json'
            },
            data: this.$extend({
              id: this.rowData.id,
              targetNodeId: this.rollbackDialog.targetNodeId,
              targetTaskId: this.rollbackDialog.targetTaskId
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '回退成功')
                  this.rollbackDialog.visible = false
                  this.loadApprovalInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.rollbackDialog.submitting = false
          })
        }).catch(() => {
          // 取消操作
        })
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 撤销
    handleRevoke () {
      this.revokeDialog.visible = true
      this.revokeDialog.loading = true
      this.revokeDialog.submitting = false
      this.revokeDialog.taskList = []
      this.revokeDialog.targetTaskId = null
      this.$nextTick(() => {
        if (this.$refs.revokeForm) {
          this.$refs.revokeForm.reset()
        }
      })
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/taskInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.revokeDialog.taskList = response.data.taskInfo || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.revokeDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.revokeDialog.visible = false
      }).finally(() => {
        this.revokeDialog.loading = false
      })
    },
    // 确认撤销
    confirmRevoke () {
      if (!this.revokeDialog.targetTaskId) {
        this.$message.warning('请选择目标人员')
        return
      }
      // 校验并获取表单数据
      this.$refs.revokeForm.validate().then(formData => {
        // 二次确认
        this.$confirm('确定要撤销此流程吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.revokeDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/revoke',
            method: 'post',
            headers: {
              'Content-Type': 'application/json'
            },
            data: this.$extend({
              id: this.rowData.id,
              targetTaskId: this.revokeDialog.targetTaskId
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '撤销成功')
                  this.revokeDialog.visible = false
                  this.loadApprovalInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.revokeDialog.submitting = false
          })
        }).catch(() => {
          // 取消操作
        })
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 重提
    handleResubmit () {
      this.$confirm('确定要重新提交此流程吗？', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
          if (action !== 'confirm') {
            done()
            return
          }
          instance.confirmButtonLoading = true
          instance.confirmButtonText = '提交中...'
          this.$ajax({
            url: '/workspace/manager/flow/db/record/process/resubmit',
            method: 'post',
            data: this.$qs.stringify({ id: this.rowData.id })
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '重提成功')
                  done()
                  this.loadApprovalInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  done()
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
            done()
          }).finally(() => {
            instance.confirmButtonLoading = false
            instance.confirmButtonText = '确定'
          })
        }
      }).catch(() => {})
    },
    // 迁移版本
    handleMigrate () {
      // 重置迁移弹窗状态
      this.migrationDialog.visible = true
      this.migrationDialog.loading = true
      this.migrationDialog.submitting = false
      this.migrationDialog.versions = []
      this.migrationDialog.targetVersion = null
      this.migrationDialog.sourceVersionInfo = null
      this.migrationDialog.targetVersionInfo = null
      this.migrationDialog.activityMapping = []

      // 加载可选版本列表
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/version',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              const processVersion = response.data.processVersion || {}
              this.migrationDialog.versions = processVersion.versions || []
              this.migrationDialog.sourceVersionInfo = processVersion.versionInfo
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              this.migrationDialog.visible = false
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
        this.migrationDialog.visible = false
      }).finally(() => {
        this.migrationDialog.loading = false
      })
    },
    // 目标版本变更时加载版本信息
    handleTargetVersionChange (definitionId) {
      if (!definitionId) {
        this.migrationDialog.targetVersionInfo = null
        this.migrationDialog.activityMapping = []
        return
      }

      this.migrationDialog.loading = true
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/versionInfo',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id,
          definitionId: definitionId
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.migrationDialog.targetVersionInfo = response.data.versionInfo
              // 构建节点映射关系
              this.buildActivityMapping()
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.migrationDialog.loading = false
      })
    },
    // 构建节点映射关系
    buildActivityMapping () {
      const sourceActivities = (this.migrationDialog.sourceVersionInfo && this.migrationDialog.sourceVersionInfo.activities) || []
      const targetActivities = (this.migrationDialog.targetVersionInfo && this.migrationDialog.targetVersionInfo.activities) || []

      // 构建目标节点的Map，方便查找
      const targetMap = {}
      targetActivities.forEach(activity => {
        targetMap[activity.id] = activity
      })

      // 生成映射关系
      const mapping = []
      sourceActivities.forEach(source => {
        // 查找ID相同的目标节点
        const target = targetMap[source.id]
        mapping.push({
          sourceActivityId: source.id,
          sourceActivityName: source.name,
          targetActivityId: target ? target.id : '',
          targetActivityName: target ? target.name : ''
        })
      })

      this.migrationDialog.activityMapping = mapping
    },
    // 映射关系变更
    handleMappingChange (row) {
      if (row.targetActivityId) {
        const targetActivity = this.targetActivities.find(activity => {
          return activity.id === row.targetActivityId
        })
        if (targetActivity) {
          row.targetActivityName = targetActivity.name
        }
      } else {
        row.targetActivityName = ''
      }
    },
    // 确认迁移
    confirmMigration () {
      if (!this.migrationDialog.targetVersion) {
        this.$message.warning('请选择目标版本')
        return
      }

      this.$confirm('确定要执行版本迁移吗？此操作不可撤销。', '确认迁移', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.migrationDialog.submitting = true
        this.$ajax({
          url: '/workspace/manager/flow/db/record/process/migrate',
          method: 'post',
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            processInstanceId: this.rowData.id,
            targetDefinitionId: this.migrationDialog.targetVersion,
            activityMapping: this.migrationDialog.activityMapping
          }
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                this.$message.success(response.data['message'] || '迁移成功')
                this.migrationDialog.visible = false
                this.loadApprovalInfo()
                this.$emit('reloadGridData')
                break
              }
              default: {
                this.$message.warning(response.data['message'])
                break
              }
            }
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.migrationDialog.submitting = false
        })
      }).catch(() => {})
    },
    // 停用/启用
    handleUpdateStatus () {
      this.updateStatusDialog.visible = true
      this.updateStatusDialog.submitting = false
    },
    confirmUpdateStatus () {
      this.updateStatusDialog.submitting = true
      const targetSuspended = !this.isSuspended
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/updateStatus',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id,
          suspended: targetSuspended
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.$message.success(response.data['message'] || (targetSuspended ? '已停用' : '已启用'))
              this.updateStatusDialog.visible = false
              this.loadApprovalInfo()
              this.$emit('reloadGridData')
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.updateStatusDialog.submitting = false
      })
    },
    // 删除
    handleDelete () {
      this.removeDialog.visible = true
      this.removeDialog.reason = ''
      this.removeDialog.includeHistoric = false
      this.removeDialog.forceDelete = false
      this.removeDialog.submitting = false
    },
    confirmDelete () {
      if (!this.removeDialog.reason) {
        this.$message.warning('请输入删除原因')
        return
      }
      this.removeDialog.submitting = true
      this.$ajax({
        url: '/workspace/manager/flow/db/record/process/remove',
        method: 'post',
        data: this.$qs.stringify({
          id: this.rowData.id,
          reason: this.removeDialog.reason,
          includeHistoric: this.removeDialog.includeHistoric,
          forceDelete: this.removeDialog.forceDelete
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.$message.success(response.data['message'] || '删除成功')
              this.removeDialog.visible = false
              this.$emit('hidePopoverAndRefresh')
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.removeDialog.submitting = false
      })
    }
  },
  mounted () {
    this.loadApprovalInfo()
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadApprovalInfo()
      }
    }
  }
}
</script>

<style lang="scss">
.flow-db-record-approval {
  height: 100%;
  width: 760px;
  max-width: 100%;

  .approval-main {
    height: 100%;
    display: flex;
    flex-direction: column;

    .approval-info {
      flex: none;
      padding: 12px 15px;
      background-color: #f5f7fa;

      .header-title {
        margin-bottom: 8px;
        display: flex;
        align-items: center;

        .process-name {
          font-size: 13px;
          font-weight: 500;
          color: #303133;
          margin-right: 10px;
        }

        .el-tag {
          margin-right: 10px;
        }
      }

      .header-info {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        font-size: 12px;

        .info-item {
          display: inline-flex;
          align-items: center;

          .info-label {
            color: #909399;

            i {
              margin-right: 1px;
              color: #909399;
            }
          }

          .info-value {
            color: #606266;
          }
        }

        .el-divider--vertical {
          margin: 0 12px;
        }
      }
    }

    .approval-tabs {
      flex: auto;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .el-tabs__header {
        margin-bottom: 0;

        .el-tabs__nav {
          margin: 0 15px;

          .el-tabs__item {
            font-size: 13px;
          }
        }
      }

      .el-tabs__content {
        flex: 1;
        overflow: hidden;

        .el-tab-pane {
          height: 100%;
        }
      }

      .tab-content {
        height: 100%;
      }
    }

    .approval-actions {
      flex: none;
      min-height: 54px;
      padding: 10px 15px;
      background-color: #fff;
      border-top: 1px solid #ebeef5;

      .flow-ended-text {
        display: flex;
        align-items: baseline;
        font-size: 12px;
        color: #909399;

        i {
          margin-right: 4px;
          font-size: 13px;
        }
      }

      .btn-danger {
        color: #f56c6c;

        &:hover {
          color: #f78989;
        }
      }

      .btn-warning {
        color: #e6a23c;

        &:hover {
          color: #ebb563;
        }
      }
    }
  }
}

.flow-db-record-approval-dialog {
  .confirm-dialog-content {
    padding: 10px 20px;

    .confirm-message {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      font-size: 14px;
      color: #606266;

      i {
        font-size: 24px;
        color: #e6a23c;
        margin-right: 12px;
      }
    }

    .confirm-hint {
      padding-left: 36px;

      .hint-text {
        margin: 0;
        font-size: 12px;
        color: #909399;
      }
    }

    .confirm-form {
      padding-left: 36px;
      margin-bottom: 16px;

      .el-form-item {
        margin-bottom: 0;
      }
    }

    .confirm-option {
      padding-left: 36px;
      margin-top: 12px;

      .hint-text {
        margin: 8px 0 0;
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-urge-dialog {
  .urge-content {
    padding: 15px 20px;
    min-height: 150px;

    .el-form-item {
      &:last-child {
        margin-bottom: 0;
      }

      .el-form-item__label {
        padding-bottom: 5px;
        line-height: 1.5;
      }
    }

    .el-textarea__inner {
      resize: none;
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-rollback-dialog {
  .rollback-content {
    padding: 15px 20px;
    min-height: 150px;
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-transfer-dialog {
  .transfer-content {
    padding: 15px 20px;
    min-height: 150px;

    .member-select-area {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .transfer-member-items {
        margin-top: 0!important;
      }

      .transfer-placeholder {
        font-size: 13px;
        color: #999;
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-parallel-multi-dialog {
  .parallel-multi-content {
    padding: 15px 20px;
    min-height: 150px;

    .member-select-area {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .parallel-multi-member-items {
        margin-top: 0!important;
      }

      .parallel-multi-placeholder {
        font-size: 13px;
        color: #999;
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-comment-dialog {
  .comment-content {
    padding: 15px 20px;
    min-height: 150px;
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-revoke-dialog {
  .revoke-content {
    padding: 15px 20px;
    min-height: 150px;
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-db-record-migration-dialog {
  .migration-content {
    padding: 15px;
    min-height: 200px;

    .version-info-section {
      margin-bottom: 16px;
      padding: 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;

      .version-info-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .version-info-item {
        flex: 1;
        display: flex;
        align-items: center;

        .version-label {
          font-size: 12px;
          color: #909399;
          margin-right: 8px;
          flex-shrink: 0;
        }

        .version-name {
          font-size: 13px;
          font-weight: 500;
          color: #303133;
          margin-right: 8px;
        }

        &.target-version-item {
          .el-select {
            width: 200px;
          }
        }
      }

      .version-arrow {
        flex: none;
        padding: 0 16px;
        font-size: 16px;
        color: #409eff;
      }
    }

    .mapping-section {
      .section-header {
        margin-bottom: 12px;

        .section-title {
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }

        .section-hint {
          font-size: 12px;
          color: #909399;
          margin-left: 8px;
        }
      }

      .mapping-arrow {
        font-size: 16px;
        color: #409eff;
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}
</style>
